'use client';

import type { PermalinkFaq } from '@/models/Product';
import { useState } from 'react';

function PlusIcon({ open }: { open: boolean }) {
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
        open
          ? 'bg-hzc_purple_500 text-white'
          : 'bg-hzc_beige_300 text-hzc_purple_500'
      }`}
      aria-hidden
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M3.5 8h9"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
        <path
          d="M8 3.5v9"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          className={`origin-center transition-transform duration-200 ${open ? 'scale-y-0' : 'scale-y-100'}`}
        />
      </svg>
    </span>
  );
}

export default function PermalinkFaqAccordion({ questions }: { questions: PermalinkFaq[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (!questions.length) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-hzc_purple_100 bg-white shadow-[0_8px_28px_rgba(43,50,128,0.06)]">
      {questions.map((item, index) => {
        const isOpen = openIndex === index;
        const isLast = index === questions.length - 1;
        return (
          <div
            key={`${item.question}-${index}`}
            className={isLast ? '' : 'border-b border-hzc_beige_400'}
          >
            <div className={isOpen ? 'bg-hzc_beige_100' : 'hover:bg-hzc_beige_100/70'}>
              <button
                type="button"
                className="flex w-full items-start gap-4 px-4 py-4 text-left transition-colors duration-150 md:px-6 md:py-5"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
              >
                <span className="mt-0.5 w-8 shrink-0 font-poppins text-[13px] font-semibold tabular-nums tracking-wide text-hzc_purple_400">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="m-0 min-w-0 flex-1 font-poppins text-[15px] font-semibold leading-6 tracking-[-0.02em] text-hzc_beige_900 md:text-base">
                  {item.question}
                </h3>
                <PlusIcon open={isOpen} />
              </button>
              <div
                className={
                  isOpen
                    ? 'px-4 pb-5 pl-[3.75rem] text-sm leading-relaxed text-hzc_beige_800 md:px-6 md:pb-6 md:pl-[4.25rem] md:text-[15px] md:leading-7'
                    : 'hidden'
                }
              >
                {item.answer}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
